import * as React from 'react'
import classNames from 'classnames'
import { Controller, useFormContext } from 'react-hook-form'
import Form from 'react-bootstrap/Form'

import './TextField.scss'

export type Props = {
  className?: string
  disabled?: boolean
  error?: string
  hideLabel?: boolean
  horizontal?: boolean
  label?: string
  maxLength?: number
  name: string
  onBlur?: () => void
  placeholder?: string
  showValidation?: boolean
  type?: 'email' | 'tel' | 'text'
}

export const TextField = React.forwardRef<HTMLInputElement, Props>(
  (
    {
      className,
      disabled = false,
      error,
      hideLabel = false,
      horizontal,
      label,
      maxLength,
      name,
      onBlur,
      placeholder,
      showValidation = true,
      type = 'text',
    },
    ref,
  ) => {
    const { control } = useFormContext()

    return (
      <Controller
        control={control}
        name={name}
        render={({ field, fieldState }) => (
          <Form.Group
            className={classNames({ 'd-flex align-items-center': horizontal }, className)}
            controlId={name}
          >
            {label && (
              <Form.Label
                className={classNames({ 'me-2 mb-0': horizontal })}
                visuallyHidden={hideLabel}
              >
                {label}
              </Form.Label>
            )}
            <Form.Control
              aria-label={label}
              disabled={disabled}
              isInvalid={
                showValidation &&
                (Boolean(error) || (fieldState.isTouched && Boolean(fieldState.error)))
              }
              maxLength={maxLength}
              name={field.name}
              onBlur={() => {
                field.onBlur()
                if (onBlur) {
                  onBlur()
                }
              }}
              onChange={field.onChange}
              placeholder={placeholder}
              ref={el => {
                field.ref(el)
                if (typeof ref === 'function') {
                  ref(el)
                } else if (ref) {
                  ref.current = el
                }
              }}
              type={type}
              value={(field.value as string | undefined) ?? ''}
            />
            {error && <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>}
          </Form.Group>
        )}
      />
    )
  },
)

TextField.displayName = 'TextField'
